import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ValueAccessorComponent } from '@pc/input';
import { LabelAnimationDirective } from '@pc/label-animation';
import { TranslateModule } from '@ngx-translate/core';
import { BusinessUnit, DropdownAutocompleteItem, PcButtonIcon, SortingConfig } from '@pc/common';
import { SpinningLoaderComponent } from '@pc/spinning-loader';
import { AutocompleteDropdownComponentData } from './autocomplete-dropdown.interface';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { OverlayModule } from '@angular/cdk/overlay';
import { CdkOverlayDynamicOffsetDirective } from '@pc/shared';

@Component({
  selector: 'pc-autocomplete-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    OverlayModule,
    FormsModule,
    LabelAnimationDirective,
    TranslateModule,
    SpinningLoaderComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkOverlayDynamicOffsetDirective
  ],
  templateUrl: './autocomplete-dropdown.component.html',
  styleUrl: './autocomplete-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteDropdownComponent),
      multi: true
    }
  ]
})
export class AutocompleteDropdownComponent extends ValueAccessorComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('inputElement') inputElement!: ElementRef;
  @Output() inputChanged = new EventEmitter<string>();
  @Output() updateValueEvent = new EventEmitter<string>();
  @Output() blurValueEvent = new EventEmitter<string>();
  public isSort = input<boolean>(false);
  public sortingConfig = input<SortingConfig>({
    sortOrder: 'asc',
    sortBy: 'text'
  });
  public labelUpDirective = viewChild<LabelAnimationDirective>(LabelAnimationDirective);
  public dropdownPanel = viewChild<ElementRef>('dropdownPanel');
  public inputBorder = viewChild<ElementRef>('inputBorder');
  public asteriskPosition = input<'before' | 'after'>('before');
  public placeholder = input<string>('');
  public label = input<string>('');
  public isMandatory = input<boolean>(false);
  public tabIndex = input<number | null>(0);
  public name = input<string>(this.cid);
  public openOnFocus = input<boolean>(false);
  public options = input.required<DropdownAutocompleteItem[]>();
  public lengthToOpenDropdown = input<number>(2);
  public noResultsText = input.required<string>();
  public isDisabled = input<boolean>(false);
  public isLoading = input<boolean>(false);
  public displayIconWithCustomText = input<boolean>(false);
  public displayChevronIcon = input<boolean>(false);
  public useIndex = input<boolean>(false);
  public ignoreImage = input<boolean>(false);
  public staticLabel = input<boolean>(false);
  public relevant = input<boolean>(false);
  public businessUnit = input(BusinessUnit.IL);
  public isSelect = signal<boolean>(false);
  public selectedImage = signal<string>('');
  public filteredOptions = signal<DropdownAutocompleteItem[]>([]);
  public oninputValue = input<{ value: string }>({ value: '' });
  public inputValue = signal<string>('');
  public isPanelOpen = signal<boolean>(false);
  public isMarkAsTouched = input<boolean>(false);
  public viewportHeight = computed(() => this.calculateViewportHeight());
  public useSingleItemFound = input<boolean>(false);
  public singleItemFound = computed(() => this.filteredOptions().length === 1 && this.isPanelOpen());
  public PcButtonIcon = PcButtonIcon;
  public isInputFocus = false;
  public useUpdateValueEvent = false;
  public BusinessUnit = BusinessUnit;
  public readonly optionLineHeight = 48;
  public readonly optionsPanelMaxHeight = 210;
  public readonly dropdownPanelId = `${this.cid}-dropdown-panel`;
  public readonly dropdownOptionIdPrefix = `${this.cid}-dropdown-option-`;
  public activeIndex = computed(() => {
    this.filteredOptions();
    this.isPanelOpen();
    return signal(-1);
  });
  public ariaActiveDescendant = computed(() => {
    const activeIndex = this.activeIndex()();
    return activeIndex === -1 ? null : `${this.dropdownOptionIdPrefix}${activeIndex}`;
  });

  constructor(private changeDetector: ChangeDetectorRef) {
    super(changeDetector);
    effect(() => {
      if (this.isMarkAsTouched()) {
        this.markAsTouched();
      }
    });

    effect(
      () => {
        if (this.oninputValue().value != '') {
          this.setInputValue(this.oninputValue().value);
        }
      },
      { allowSignalWrites: true }
    );
  }

  @HostBinding('class')
  get hostClasses(): string {
    return 'scrollbar-grey';
  }

  @HostListener('keydown', ['$event'])
  onKeydown($event: KeyboardEvent) {
    const direction = { ArrowDown: 1, ArrowUp: -1 }[$event.key] ?? 0;
    const optionsLength = this.filteredOptions().length;

    if (!direction || !optionsLength) return;

    if (!this.isPanelOpen()) {
      this.openList();
    }

    this.activeIndex().update((current) => {
      return (Math.max(current + direction, -1) + optionsLength) % optionsLength;
    });

    $event.preventDefault();
  }

  public ngOnInit(): void {
    this.setOptionsAndLabelAnimation();
  }

  public ngAfterViewInit(): void {
    // Using requestAnimationFrame to ensure the element is rendered on the screen
    requestAnimationFrame(() => {
      if (this.oninputValue().value != '') {
        this.setInputValue(this.oninputValue().value);
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.handleOptionsChange(changes);
  }

  public onOverlayKeydown($event: KeyboardEvent) {
    const options = this.filteredOptions();
    if ($event.key === 'Escape') {
      this.clearFormAndClose();
    } else if ($event.key === 'Enter') {
      this.selectOption(options[this.activeIndex()()]);
    }
  }

  public copyConstructor(component: AutocompleteDropdownComponent, dropdownAutocompleteData: unknown): void {
    if (dropdownAutocompleteData) {
      const data = dropdownAutocompleteData as AutocompleteDropdownComponentData;
      if (data.isMandatory) {
        component.isMandatory = data.isMandatory;
      }
      if (data.placeholder) {
        component.placeholder = data.placeholder;
      }
      if (data.label) {
        component.label = data.label;
      }
      if (data.name) {
        component.name = data.name;
      }
      if (data.lengthToOpenDropdown) {
        component.lengthToOpenDropdown = data.lengthToOpenDropdown;
      }
      if (data.noResultsText) {
        component.noResultsText = data.noResultsText;
      }
      if (data.isDisabled) {
        component.isDisabled = data.isDisabled;
      }
      if (data.isLoading) {
        component.isLoading = data.isLoading;
      }
      if (data.staticLabel) {
        component.staticLabel = data.staticLabel;
      }
      if (data.options) {
        component.options = data.options;
      }
      if (data.useUpdateValueEvent) {
        component.useUpdateValueEvent = data.useUpdateValueEvent;
      }
      this.changeDetector.markForCheck();
    }
  }

  public filterOptions(value: string): void {
    this.inputChanged.emit(value);
    this.filteredOptions.set(
      this.sortOptions(this.options().filter((option) => option.text.toLowerCase().includes(value.toLowerCase())))
    );

    this.isPanelOpen.set(value.length >= this.lengthToOpenDropdown());
    if (value.length === 1) {
      this.labelUpDirective()?.approveContentAnimation();
    } else if (value.length < 1) {
      this.labelUpDirective()?.clearContentAnimation();
    }
  }

  public selectOption(option: DropdownAutocompleteItem): void {
    if (option) {
      this.value = option.value;
      this.filteredOptions.set(this.options());
      this.onChange(option.value);
      this.inputValue.set(option.displayIconWithCustomText ?? option.text);
      this.isPanelOpen.set(false);
      this.updateValueEvent.emit(option.value as string);
      this.isSelect.set(true);
      this.setSelectedImage(option.value.toString());
      this.labelUpDirective()?.approveContentAnimation();
    }
  }

  override writeValue(value: string): void {
    const selectedOption = this.options().find((option) => option.value === value);
    if (selectedOption) {
      this.inputValue.set(selectedOption.text);
      this.value = selectedOption.value;
    } else if (value == '') {
      this.inputValue.set('');
      this.value = value;
    } else if (value == null) {
      this.setInputValue(this.inputValue(), false);
    }
  }

  public onInputBlur(): void {
    if (!this.inputValue()) {
      this.labelUpDirective()?.clearContentAnimation();
    }
    this.blurValueEvent.emit(this.inputValue());
    this.isInputFocus = false;
    this.markAsTouched();
  }

  public onInputFocus(): void {
    this.isInputFocus = true;
    if (this.openOnFocus()) {
      this.openList(true);
    }
  }

  public closeList() {
    this.isPanelOpen.set(false);
    this.markAsTouched();
  }

  public clearFormAndClose() {
    this.isPanelOpen.set(false);
    this.formControl?.setValue(null);
    this.validate();
  }

  public openList(setFocus = false) {
    if (setFocus) {
      this.isInputFocus = true;
    }
    !this.inputElement?.nativeElement.focus();
    if (this.inputValue() !== '') {
      this.filteredOptions.set(
        this.sortOptions(
          this.options().filter((option) => option.text.toLowerCase().includes(this.inputValue().toLowerCase()))
        )
      );
    } else {
      this.filteredOptions.set(this.sortOptions(this.options()));
    }
    this.isPanelOpen.set(true);
    if (this.filteredOptions().length || this.inputValue().length > 1) {
      this.labelUpDirective()?.approveContentAnimation();
    } else if (this.inputValue().length < 1) {
      this.labelUpDirective()?.clearContentAnimation();
    }
    this.markAsTouched();
  }

  public onInputKeyUp($event: KeyboardEvent) {
    if ($event.key === 'Enter') return;

    if (this.isSelect()) {
      this.isSelect.set(false);
    }
    if ($event.key === 'Backspace' || $event.key === 'Delete') {
      this.onChange(null);
      this.value = null;
    }
    this.markAsTouched();
  }

  public onClearForm(): void {
    this.writeValue('');
    this.markAsUntouched();
    this.labelUpDirective()?.clearContentAnimation();
  }

  public trackByFn(index: number, item: DropdownAutocompleteItem): string {
    return index.toString() + item.value.toString();
  }

  private setOptionsAndLabelAnimation() {
    this.filteredOptions.set(this.sortOptions([...this.options()]));
    if (this.formControl?.value) {
      this.labelUpDirective()?.approveContentAnimation();
    }
  }

  private handleOptionsChange(changes: SimpleChanges) {
    if (changes['options'] && !changes['options'].firstChange) {
      this.filteredOptions.set(this.sortOptions([...this.options()]));
      if (this.formControl?.value) {
        this.selectOption(
          this.options().find(
            (option) => option.value.toString() === this.formControl?.value?.toString()
          ) as DropdownAutocompleteItem
        );
        if (this.formControl?.value) {
          this.labelUpDirective()?.approveContentAnimation();
        }
      }
    }
  }

  private setSelectedImage(value: string) {
    if (this.isSelect() && this.displayIconWithCustomText()) {
      this.selectedImage.set(this.options().find((option) => option.value.toString() === value)?.image ?? '');
    } else {
      this.selectedImage.set('');
    }
  }

  private setInputValue(search: string, byValue = true) {
    let value: DropdownAutocompleteItem | undefined;
    if (byValue) {
      value = this.options().find((x) => x.value.toString() === search);
    } else if (this.displayIconWithCustomText()) {
      value = this.options().find((x) => x.displayIconWithCustomText?.toString() === search);
    } else {
      value = this.options().find((x) => x.text.toString() === search);
    }
    if (value) {
      this.isSelect.set(true);
      this.selectedImage.set(value.image ?? '');
      this.formControl?.setValue(value.value);
      this.validate();
      this.inputValue.set(
        this.displayIconWithCustomText() ? (value.displayIconWithCustomText ?? '') : (value.text ?? '')
      );

      this.labelUpDirective()?.approveContentAnimation();
    } else {
      this.isSelect.set(false);
      this.selectedImage.set('');
      this.formControl?.setValue('');
      this.inputValue.set('');
      this.labelUpDirective()?.clearContentAnimation();
    }
  }

  private calculateViewportHeight(): number {
    const totalHeight = this.filteredOptions().length * this.optionLineHeight;
    return Math.min(totalHeight, this.optionsPanelMaxHeight);
  }

  private sortOptions(options: DropdownAutocompleteItem[]): DropdownAutocompleteItem[] {
    if (!this.isSort()) {
      return options;
    }

    const { sortBy, sortOrder } = this.sortingConfig();

    return [...options].sort((a, b) => {
      const aKey = a[sortBy]?.toString().toLowerCase() ?? '';
      const bKey = b[sortBy]?.toString().toLowerCase() ?? '';

      if (sortOrder === 'asc') {
        return aKey.localeCompare(bKey);
      } else {
        return bKey.localeCompare(aKey);
      }
    });
  }
}
